import _ from 'underscore';
import $ from 'jquery';
import { AccountUtils } from '@bingads-webui-clientcenter/entity-utils';

import { AccountsMode } from './constants';

export const changeEvents = (...attributes) => _.map(attributes, attr => `change:${attr}`).join(' ');

export function formatDate(date) {
  const year = date.getFullYear();
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const day = date.getDate().toString().padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export function getFilteredTotalsMetrics({
  data,
  metric,
}) {
  if (data.raw) {
    const filteredTotals = data.raw['@ns.filtered.totals'];

    if (filteredTotals && filteredTotals[metric]) {
      return filteredTotals[metric];
    }

    const unfilteredTotals = data.raw['@ns.unfiltered.totals'];

    if (unfilteredTotals && unfilteredTotals[metric]) {
      return unfilteredTotals[metric];
    }
  } else if (data.OverallTotals) {
    if (data.DeviceSegmentedPerformanceData && data.DeviceSegmentedPerformanceData[metric]) {
      // eslint-disable-next-line no-param-reassign
      data.OverallTotals[metric] = _.extend(
        {},
        data.OverallTotals[metric],
        {
          SegmentedData: data.DeviceSegmentedPerformanceData[metric].SegmentedData,
        }
      );
    }
    return data.OverallTotals[metric];
  }

  return null;
}

function upperLevelNavigation(entitySet) {
  return entitySet.navigation.source.navigation;
}

export function coerceStatus(status, AccountInactiveReasons) {
  if (AccountInactiveReasons) {
    const {
      activityStatus,
    } = AccountUtils;
    return activityStatus({ AccountInactiveReasons });
  }

  // Here we are coercing status for both Campaigns and Adgroups for status template.
  // This must match what we do in Campaign and Ad-group grid.
  // So we are showing 'CampaignUserPaused', 'CampaignBudgetPaused' and 'CampaignBudgetUserPaused'
  // as active for adgroups.
  if (_.contains([
    'UserPaused',
    'BudgetUserPaused',
  ], status)) {
    return 'Paused';
  }

  return status;
}

export function entityHash({
  id,
  typeName,
  entitySet,
  navigationLevel,
  adGroup,
  campaign,
  isSmartCampaign,
  status,
  pageContext,
  legacyRouter,
}) {
  let hash;
  const {
    CurrentUser: {
      Id: uid,
    },
    CurrentCustomer: {
      Id: cid,
    },
    CurrentAccount: {
      Id: aid,
    },
  } = pageContext;

  switch (typeName) {
    case 'Account':
      // eslint-disable-next-line camelcase
      hash = legacyRouter.getRouteUrl('account_$summary', { account_id: id });
      break;
    case 'Campaign':
      if (isSmartCampaign) {
        if (status === 'InProgress') {
          hash = `/Campaign/Smart?uid=${uid}&aid=${aid}&cid=${cid}#/CreateCampaign/${id}`;
        } else {
          hash = `/Campaign/Smart?uid=${uid}&aid=${aid}&cid=${cid}#/campaign/${id}`;
        }
      } else {
        // eslint-disable-next-line camelcase
        hash = legacyRouter.getRouteUrl('campaign_$summary', { campaign_id: id });
      }
      break;
    case 'AdGroup':
      // eslint-disable-next-line camelcase
      hash = legacyRouter.getRouteUrl('adgroup_$summary', {
        campaign_id: upperLevelNavigation(entitySet).key,
        adgroup_id: id,
      });
      break;
    case 'Keyword':
      if (navigationLevel === 'account') {
        hash = legacyRouter.getRouteUrl('keyword');
      } else {
        // eslint-disable-next-line camelcase
        hash = legacyRouter.getRouteUrl('adgroup_keyword', { campaign_id: campaign.Id, adgroup_id: adGroup.Id });
      }
      break;
    case 'SearchTerm':
      if (navigationLevel === 'account') {
        hash = legacyRouter.getRouteUrl('keyword_search_term');
      } else {
        // eslint-disable-next-line camelcase
        hash = legacyRouter.getRouteUrl('campaign_keyword_search_term', { campaign_id: campaign.Id });
      }
      break;
    default:
      hash = '';
  }

  return hash;
}

export function getEntityIds({
  typeName,
  id,
  entitySet,
  campaign,
  adGroup,
}) {
  let campaignId = '';
  let adgroupId = '';
  let accountId = '';

  switch (typeName) {
    case 'Account':
      accountId = id;
      break;
    case 'Campaign':
      campaignId = id;
      break;
    case 'AdGroup':
      adgroupId = id;
      campaignId = upperLevelNavigation(entitySet).key;
      break;
    case 'Keyword':
    case 'SearchTerm':
      campaignId = campaign.Id;
      adgroupId = adGroup.Id;
      break;
    default:
  }

  return {
    campaignId,
    adgroupId,
    accountId,
  };
}

function saveToFilterStorage({
  entityType,
  text,
  createEditableFilterStorage,
}) {
  const filterStorage = createEditableFilterStorage(entityType);

  filterStorage.save({
    id: null,
    name: null,
    query: {
      Text: {
        $eq: text,
      },
    },
  });
}

export function navigateToEntity({
  event,
  pageContext,
  legacyRouter,
  createEditableFilterStorage,
  instrumentation,
  constants,
  cardType,
}) {
  const typeName = $(event.currentTarget).data('type');
  const navigationLevel = $(event.currentTarget).data('navigationlevel');

  instrumentation.logMessage([constants.LOGCATEGORY.TRACE], constants.LOGLEVEL.INFO, cardType, `${navigationLevel} level ${typeName} type entity link clicked.`);

  if (event && (event.button !== 0 ||
    event.ctrlKey ||
    event.shiftKey ||
    event.altKey ||
    event.metaKey)) {
    return true;
  }
  const campaignId = $(event.currentTarget).data('campaignid');
  const adgroupId = $(event.currentTarget).data('adgroupid');
  const text = $(event.currentTarget).data('text');
  const campaignType = $(event.currentTarget).data('campaigntype');
  const rawStatus = $(event.currentTarget).data('rawstatus');
  const {
    CurrentCustomer: {
      Id: cid,
    },
    CurrentUser: {
      Id: uid,
    },
  } = pageContext;
  let returnValue = false;

  switch (typeName) {
    case 'Account': {
      const accountId = $(event.currentTarget).data('accountid');
      // eslint-disable-next-line camelcase
      window.location.href = legacyRouter.getRouteUrl('account_$summary', { account_id: accountId });
      break;
    }
    case 'Campaign': {
      const {
        CurrentAccount: {
          Id: aid,
        },
      } = pageContext;
      if (campaignType === 'Smart') {
        if (rawStatus === 'InProgress') {
          window.location.href = `/Campaign/Smart?uid=${uid}&cid=${cid}&aid=${aid}#/CreateCampaign/${campaignId}`;
        } else {
          window.location.href = `/Campaign/Smart?uid=${uid}&cid=${cid}&aid=${aid}#/campaign/${campaignId}`;
        }
      } else {
        // eslint-disable-next-line camelcase
        legacyRouter.navigateToRoute('campaign_$summary', { campaign_id: campaignId });
      }
      break;
    }
    case 'AdGroup':
      // eslint-disable-next-line camelcase
      legacyRouter.navigateToRoute('adgroup_$summary', { campaign_id: campaignId, adgroup_id: adgroupId });
      break;
    case 'Keyword':
      saveToFilterStorage({
        entityType: 'keyword',
        text,
        createEditableFilterStorage,
      });
      if (navigationLevel === 'account') {
        legacyRouter.navigateToRoute('keyword');
      } else {
        // eslint-disable-next-line camelcase
        legacyRouter.navigateToRoute('campaign_keyword', { campaign_id: campaignId });
      }
      break;
    case 'SearchTerm':
      saveToFilterStorage({
        entityType: 'searchTerm',
        text,
        createEditableFilterStorage,
      });
      if (navigationLevel === 'account') {
        legacyRouter.navigateToRoute('keyword_search_term');
      } else {
        // eslint-disable-next-line camelcase
        legacyRouter.navigateToRoute('campaign_keyword_search_term', { campaign_id: campaignId });
      }
      break;
    default:
      returnValue = true;
  }

  return returnValue;
}

export function getLocalizedSupportLink(pageContext) {
  const culture = pageContext.Culture.toLowerCase();

  // Default to en-us.
  let consultationLink = 'https://help.ads.microsoft.com/apex/index/3/en-us/60029';
  let coachingLink = 'https://advertise.bingads.microsoft.com/en-us/resources/support/coaching';

  // Get culture specific pages if we have one.
  switch (culture) {
    case 'en-gb':
      consultationLink = 'https://help.ads.microsoft.com/apex/index/3/en-gb/60029';
      coachingLink = 'https://advertise.bingads.microsoft.com/en-gb/resources/support/coaching';
      break;
    case 'fr-fr':
      consultationLink = 'https://help.ads.microsoft.com/apex/index/3/fr-fr/60029';
      coachingLink = 'https://advertise.bingads.microsoft.com/fr-fr/ressources/support/un-coach-pour-demarrer';
      break;
    case 'de-de':
      consultationLink = 'https://help.ads.microsoft.com/apex/index/3/de-de/60029';
      coachingLink = 'https://advertise.bingads.microsoft.com/de-de/Ressourcen/support/coaching';
      break;
    case 'en-au':
      consultationLink = 'https://help.ads.microsoft.com/apex/index/3/en-au/60029';
      coachingLink = 'https://advertise.bingads.microsoft.com/en-au/resources/support/coaching';
      break;
    case 'en-nz':
      consultationLink = 'https://help.ads.microsoft.com/apex/index/3/en-nz/60029';
      coachingLink = 'https://advertise.bingads.microsoft.com/en-nz/resources/support/coaching';
      break;
    case 'pt-br':
      consultationLink = 'https://help.ads.microsoft.com/apex/index/3/pt-br/60029';
      coachingLink = 'https://advertise.bingads.microsoft.com/pt-br/recursos/suporte/coaching';
      break;
    case 'it-it':
      consultationLink = 'https://help.ads.microsoft.com/apex/index/3/it-it/60029';
      coachingLink = 'https://advertise.bingads.microsoft.com/it-it/risorse/supporto-di/coaching';
      break;
    case 'nl-nl':
      consultationLink = 'https://help.ads.microsoft.com/apex/index/3/nl-nl/60029';
      coachingLink = 'https://advertise.bingads.microsoft.com/nl-nl/hulpmiddelen/ondersteuning/coaching';
      break;
    case 'en-in':
      consultationLink = 'https://help.ads.microsoft.com/apex/index/3/en-in/60029';
      coachingLink = 'https://advertise.bingads.microsoft.com/en-in/resources/support/coaching';
      break;
    case 'es-es':
      consultationLink = 'https://help.ads.microsoft.com/apex/index/3/es-es/60029';
      coachingLink = 'https://advertise.bingads.microsoft.com/es-es/recursos/soporte/coaching';
      break;
    default:
      if (culture.indexOf('es') !== -1) {
        consultationLink = 'https://help.ads.microsoft.com/apex/index/3/es-mx/60029';
        coachingLink = 'https://advertise.bingads.microsoft.com/es-mx/recursos/soporte/coaching';
      }
  }

  return {
    consultationLink: `${consultationLink}?id=optimization-consultation-tip`,
    coachingLink: `${coachingLink}?id=onboarding-consultation-tip`,
  };
}

function periodDurationIn(dateUtil, { start, end } = {}, max, min = 0) {
  if (!_.isDate(start) || !_.isDate(end)) {
    return true;
  }

  const duration = dateUtil.diff(start, end, 'day');

  return duration >= min && duration <= max;
}

function periodFit(dateUtil, period, max, min = 0) {
  return periodDurationIn(dateUtil, period, max, min) &&
    periodDurationIn(dateUtil, period.compared || {}, max);
}

export function availableGranularities(period = {}, dateUtil, accountsMode = AccountsMode.Expert) {
  const {
    comparativePeriod = {},
  } = period;

  const result = [];

  const hourlyStartDate = new Date();

  hourlyStartDate.setMonth(hourlyStartDate.getMonth() - 6);

  if (periodFit(dateUtil, period, 0)) {
    if (periodFit(dateUtil, {
      start: hourlyStartDate,
      end: period.start,
      compared: {
        start: hourlyStartDate,
        end: comparativePeriod.end,
      },
    }, Infinity) && accountsMode === AccountsMode.Expert) {
      result.push('Hour');
    } else {
      // we only support hourly data for a sliding 6 months window
      result.push('Day');
    }
  }

  if (periodFit(dateUtil, period, 30, 1)) {
    result.push('Day');
  }

  if (periodFit(dateUtil, period, 210, 7)) {
    result.push('Week');
  }

  if (periodFit(dateUtil, period, 900, 31)) {
    result.push('Month');
  }

  if (periodDurationIn(dateUtil, period, Infinity, 90)) {
    result.push('Quarter');
  }

  return result;
}
export const getPopDescription = (i18n, comparisonDateRange) => {
  let comparisonDateRangeDateRangeText;
  let comparisonText;

  if (comparisonDateRange.rangeInDays <= 7) {
    comparisonText = i18n.getString('Dashboard_TopChanges_Period_LastWeek');
  } else {
    comparisonText = i18n.getString('Dashboard_TopChanges_Period_Previous_Period');
  }

  // eslint-disable-next-line no-param-reassign
  comparisonDateRange.formattedStart = i18n.formatDate(comparisonDateRange.start);
  // eslint-disable-next-line no-param-reassign
  comparisonDateRange.formattedEnd = i18n.formatDate(comparisonDateRange.end);

  if (comparisonDateRange.formattedStart === comparisonDateRange.formattedEnd) {
    comparisonDateRangeDateRangeText = `(${comparisonDateRange.formattedStart})`;
  } else {
    comparisonDateRangeDateRangeText = `(${comparisonDateRange.formattedStart} - ${comparisonDateRange.formattedEnd})`;
  }

  let comparisonDateRangeDescription = comparisonText.toLowerCase();

  if (comparisonDateRange.key === 'customPeriod') {
    comparisonDateRangeDescription = '';
  }

  return `${i18n.getString('Dashboard_TopChanges_Period_Comparison')}${comparisonDateRangeDescription} ${comparisonDateRangeDateRangeText}`;
};
